/**
 * useRoutingEvents Hook
 * 
 * React hook for subscribing to the Routing Event Store.
 * Uses useSyncExternalStore for optimal React 18 integration.
 */

import { useSyncExternalStore, useMemo, useCallback } from 'react';
import {
  getAllRoutingEvents,
  subscribeToRoutingEvents,
  clearEvents,
  clearEventsBySource,
  type RoutingEvent,
  type RoutingEventSource,
  type RoutingEventOutcome,
  type RoutingEventFilter,
  type RoutingEventStats,
} from '@/lib/routingEvents';

/**
 * Get a snapshot of all events for useSyncExternalStore
 */
function getSnapshot(): RoutingEvent[] {
  return getAllRoutingEvents();
}

/**
 * Server snapshot (for SSR, returns empty array)
 */
function getServerSnapshot(): RoutingEvent[] {
  return [];
}

/**
 * Hook to access all routing events with automatic re-rendering on changes
 */
export function useRoutingEvents(): RoutingEvent[] {
  return useSyncExternalStore(
    subscribeToRoutingEvents,
    getSnapshot,
    getServerSnapshot
  );
}

/**
 * Hook to get recent routing events
 */
export function useRecentRoutingEvents(limit: number = 50): RoutingEvent[] {
  const allEvents = useRoutingEvents();
  return useMemo(() => {
    return [...allEvents].reverse().slice(0, limit);
  }, [allEvents, limit]);
}

/**
 * Hook to get filtered routing events
 */
export function useFilteredRoutingEvents(filter: RoutingEventFilter): RoutingEvent[] {
  const allEvents = useRoutingEvents();
  return useMemo(() => {
    return allEvents.filter(event => {
      if (filter.source && event.source !== filter.source) return false;
      if (filter.outcome && event.outcome !== filter.outcome) return false;
      if (filter.leadType && event.lead.type !== filter.leadType) return false;
      if (filter.leadState && event.lead.state !== filter.leadState) return false;
      if (filter.agentId && event.winner?.agentId !== filter.agentId) return false;
      if (filter.simulationRunId && event.metadata.simulationRunId !== filter.simulationRunId) return false;
      if (filter.fromTimestamp && event.timestamp < filter.fromTimestamp) return false;
      if (filter.toTimestamp && event.timestamp > filter.toTimestamp) return false;
      return true;
    });
  }, [allEvents, filter]);
}

/**
 * Hook to get routing events by source
 */
export function useRoutingEventsBySource(source: RoutingEventSource): RoutingEvent[] {
  const allEvents = useRoutingEvents();
  return useMemo(() => {
    return allEvents.filter(e => e.source === source);
  }, [allEvents, source]);
}

/**
 * Hook to get routing events by outcome
 */
export function useRoutingEventsByOutcome(outcome: RoutingEventOutcome): RoutingEvent[] {
  const allEvents = useRoutingEvents();
  return useMemo(() => {
    return allEvents.filter(e => e.outcome === outcome);
  }, [allEvents, outcome]);
}

/**
 * Hook to get routing event statistics
 */
export function useRoutingEventStats(): RoutingEventStats {
  const allEvents = useRoutingEvents();
  return useMemo(() => {
    const total = allEvents.length;
    
    const byOutcome: Record<RoutingEventOutcome, number> = {
      ROUTED: 0,
      UNSOLD: 0,
      RETRY_QUEUED: 0,
      STOCKED: 0,
      VALIDATION_FAILED: 0,
    };
    
    const bySource: Record<RoutingEventSource, number> = {
      SIMULATION: 0,
      LIVE: 0,
    };
    
    let totalDuration = 0;
    
    for (const event of allEvents) {
      byOutcome[event.outcome]++;
      bySource[event.source]++;
      totalDuration += event.metadata.durationMs;
    }
    
    const routingSuccessRate = total > 0 
      ? (byOutcome.ROUTED / total) * 100 
      : 0;
    
    const averageDurationMs = total > 0 
      ? totalDuration / total 
      : 0;
    
    return {
      total,
      byOutcome,
      bySource,
      routingSuccessRate,
      averageDurationMs,
    };
  }, [allEvents]);
}

/**
 * Hook providing event store actions
 */
export function useRoutingEventActions() {
  const clearAll = useCallback(() => {
    clearEvents();
  }, []);
  
  const clearBySource = useCallback((source: RoutingEventSource) => {
    clearEventsBySource(source);
  }, []);
  
  return {
    clearAll,
    clearBySource,
  };
}
